/**
 * ShellTopbar — Sprint 6.1 (UX/UI Refactor NOC).
 *
 * Barra superior fina do novo shell. Diferente do Topbar antigo
 * (que carregava o menu inteiro), aqui só ficam:
 *  - botão de menu (mobile) / título da view atual
 *  - busca global (campo que abre o command palette)
 *  - quick actions (atalhos de ação)
 *  - status do backend + último refresh + botão atualizar
 *
 * O menu de navegação migrou para a Sidebar.
 */
import { useState } from "react";
import NavIcon from "../../nav/NavIcon.jsx";
import { QUICK_ACTIONS } from "../../nav/navConfig.js";
import { viewAllowed } from "../../lib/rbac.js";
import StatusBadge from "../StatusBadge.jsx";
import AccountModal from "../AccountModal.jsx";
import { t } from "../../i18n/index.js";

export default function ShellTopbar({
  viewTitle,
  backendOk,
  lastRefresh,
  refreshing,
  onRefresh,
  onOpenSearch,
  onOpenMobileMenu,
  onQuickAction,
  session = null,
  onLogout,
}) {
  const [accountOpen, setAccountOpen] = useState(false);
  return (
    <header className="ipwd-topbar sticky top-0 z-30 h-14 flex items-center
                       gap-3 px-3 sm:px-6 border-b border-noc-border
                       bg-noc-bg/95 backdrop-blur">
      {/* Menu mobile */}
      <button
        onClick={onOpenMobileMenu}
        className="lg:hidden flex items-center justify-center w-9 h-9
                   rounded-md text-noc-muted hover:text-noc-text
                   hover:bg-noc-panel2 transition-colors shrink-0"
        title={t("shell.menu")}
      >
        <NavIcon name="menu" size={20} />
      </button>

      {/* Breadcrumb da view atual — estilo console */}
      <div className="hidden sm:flex items-center gap-2 shrink-0
                      font-mono text-[11px] tracking-[0.12em]">
        <span className="text-noc-accent">NOC</span>
        <span className="text-noc-faint">//</span>
        <span className="text-noc-text2 uppercase">{viewTitle}</span>
      </div>

      {/* Busca global — campo que abre o command palette */}
      <button
        onClick={onOpenSearch}
        className="ipwd-search-trigger flex items-center gap-2 flex-1
                   max-w-md mx-auto px-3 h-9 rounded-lg
                   border border-noc-border bg-noc-panel
                   text-noc-muted hover:border-noc-accent/40
                   transition-colors"
      >
        <NavIcon name="search" size={15} className="shrink-0" />
        <span className="text-sm flex-1 text-left truncate">
          {t("shell.searchPlaceholder")}
        </span>
        <kbd className="hidden sm:inline-block font-mono text-[10px]
                        text-noc-faint border border-noc-border
                        rounded px-1.5 py-0.5">
          ⌘K
        </kbd>
      </button>

      {/* Quick actions */}
      <div className="hidden md:flex items-center gap-1 shrink-0">
        {QUICK_ACTIONS.filter((qa) => viewAllowed(qa.view)).map((qa) => (
          <button
            key={qa.id}
            onClick={() => onQuickAction(qa.view)}
            title={t(qa.labelKey)}
            className="flex items-center justify-center w-9 h-9
                       rounded-md text-noc-muted hover:text-noc-accent
                       hover:bg-noc-panel2 transition-colors"
          >
            <NavIcon name={qa.icon} size={17} />
          </button>
        ))}
        <div className="w-px h-5 bg-noc-border mx-1" />
      </div>

      {/* Status + refresh */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <div className="hidden sm:block">
          {backendOk === null ? (
            <span className="text-xs text-noc-muted">
              {t("common.loading")}
            </span>
          ) : backendOk ? (
            <StatusBadge status="online" />
          ) : (
            <StatusBadge status="offline" />
          )}
        </div>
        {lastRefresh && (
          <span className="hidden lg:inline text-[11px] font-mono">
            <span className="text-noc-faint">SYNC </span>
            <span className="text-noc-text2">
              {lastRefresh.toLocaleTimeString()}
            </span>
          </span>
        )}
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center justify-center w-9 h-9 rounded-md
                     text-noc-muted hover:text-noc-text
                     hover:bg-noc-panel2 transition-colors
                     disabled:opacity-50"
          title={t("shell.refresh")}
        >
          <NavIcon
            name="refresh"
            size={16}
            className={refreshing ? "ipwd-spin" : ""}
          />
        </button>

        {/* S3.2 — usuário logado + logout */}
        {onLogout && (
          <>
            <div className="w-px h-5 bg-noc-border mx-1" />
            {session?.email && (
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className="text-xs text-noc-text font-medium truncate
                                 max-w-[160px]">
                  {session.email}
                </span>
                <span className="text-[10px] text-noc-muted truncate
                                 max-w-[160px]">
                  {session.role}
                  {session.tenantName ? ` · ${session.tenantName}` : ""}
                </span>
              </div>
            )}
            {session?.email && (
              <button
                onClick={() => setAccountOpen(true)}
                className="flex items-center justify-center w-9 h-9 rounded-md
                           text-noc-muted hover:text-noc-text
                           hover:bg-noc-panel2 transition-colors"
                title="Minha conta"
              >
                <NavIcon name="gear" size={16} />
              </button>
            )}
            <button
              onClick={onLogout}
              className="flex items-center justify-center w-9 h-9 rounded-md
                         text-noc-muted hover:text-noc-err
                         hover:bg-noc-panel2 transition-colors"
              title="Sair"
            >
              <NavIcon name="logout" size={16} />
            </button>
            <AccountModal
              open={accountOpen}
              session={session}
              onClose={() => setAccountOpen(false)}
              onLogout={onLogout}
            />
          </>
        )}
      </div>
    </header>
  );
}
